import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { IconName } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FaIcon, getAllSolidIconNames } from "../TagChip"

import {
  Add,
  CheckCircle,
  Close,
  Delete,
  Edit,
  Label,
  PhoneIphone,
  Refresh,
  Search,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetAllTagsQuery,
  useUpdateTagMutation,
} from "../../apis/appApi"

type TagFormData = {
  label: string
  shortLabel: string
  type: string
  color: string
  icon: string
  displayOption: string
  onMobile: boolean
}

const emptyForm: TagFormData = {
  label: "",
  shortLabel: "",
  type: "ORDER",
  color: "#1976d2",
  icon: "tag",
  displayOption: "",
  onMobile: false,
}

const ICONS_PER_ROW = 8
const ICON_CELL = 40 // px
const GRID_GAP = 4 // px (0.5 * 8)
const ROW_HEIGHT = ICON_CELL + GRID_GAP
const GRID_HEIGHT = 280

/** Virtualised icon grid — only renders rows in the viewport. */
const VirtualIconGrid: FC<{
  icons: IconName[]
  selected: string
  onSelect: (name: IconName) => void
}> = ({ icons, selected, onSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)

  const totalRows = Math.ceil(icons.length / ICONS_PER_ROW)
  const totalHeight = totalRows * ROW_HEIGHT
  const startRow = Math.floor(scrollTop / ROW_HEIGHT)
  const visibleRows = Math.ceil(GRID_HEIGHT / ROW_HEIGHT) + 1
  const endRow = Math.min(startRow + visibleRows, totalRows)

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop)
    }
  }, [])

  if (icons.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", py: 3 }}
      >
        No icons found
      </Typography>
    )
  }

  const visibleIcons: { name: IconName; style: React.CSSProperties }[] = []
  for (let row = startRow; row < endRow; row++) {
    for (let col = 0; col < ICONS_PER_ROW; col++) {
      const idx = row * ICONS_PER_ROW + col
      if (idx >= icons.length) break
      visibleIcons.push({
        name: icons[idx],
        style: {
          position: "absolute",
          top: row * ROW_HEIGHT,
          left: col * (ICON_CELL + GRID_GAP),
          width: ICON_CELL,
          height: ICON_CELL,
        },
      })
    }
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        height: GRID_HEIGHT,
        overflowY: "auto",
        position: "relative",
        mx: 1.5,
        mb: 1.5,
      }}
    >
      <Box sx={{ height: totalHeight, position: "relative" }}>
        {visibleIcons.map(({ name, style }) => (
          <Tooltip key={name} title={name} enterDelay={400}>
            <Box
              onClick={() => onSelect(name)}
              style={style}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                cursor: "pointer",
                bgcolor: selected === name ? "primary.50" : "transparent",
                border: selected === name ? 2 : 0,
                borderColor: "primary.main",
                color: selected === name ? "primary.main" : "text.secondary",
                "&:hover": {
                  bgcolor: selected === name ? "primary.100" : "grey.100",
                },
              }}
            >
              <FontAwesomeIcon icon={["fas", name]} style={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  )
}

/** Icon picker: scrollable grid with local search. */
const IconPicker: FC<{
  value: string
  color: string
  onChange: (icon: string) => void
}> = ({ value, color, onChange }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState("")
  const [ready, setReady] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  const open = Boolean(anchorEl)

  // Defer icon loading to next frame so popover opens instantly
  useEffect(() => {
    if (open) {
      setReady(false)
      const id = requestAnimationFrame(() => setReady(true))
      return () => cancelAnimationFrame(id)
    }
  }, [open])

  const allIcons = useMemo(() => (ready ? getAllSolidIconNames() : []), [ready])

  const filtered = useMemo(() => {
    if (!search.trim()) return allIcons
    const q = search.toLowerCase()
    return allIcons.filter((name) => name.includes(q))
  }, [allIcons, search])

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setSearch("")
  }

  const handleSelect = (iconName: IconName) => {
    onChange(iconName)
    setAnchorEl(null)
  }

  return (
    <>
      {/* Trigger button showing current icon */}
      <Box
        data-testid="tag-icon-picker"
        onClick={handleOpen}
        sx={{
          border: 1,
          borderColor: open ? "primary.main" : "divider",
          borderRadius: 1,
          px: 2,
          py: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 56,
          "&:hover": { borderColor: "text.primary" },
          transition: "border-color 0.2s",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FaIcon icon={value} color="#fff" size={18} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t("configuration.tags.icon")}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
            {value || "tag"}
          </Typography>
        </Box>
      </Box>

      {/* Popover with search + icon grid */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { ...uiPaper("UI-DLV2-178"), sx: { width: 380, borderRadius: 2, overflow: "hidden" } },
        }}
        TransitionProps={{
          onEntered: () => searchRef.current?.focus(),
        }}
      >
        {/* Search */}
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            inputRef={searchRef}
            placeholder={String(t("configuration.tags.iconSearch"))}
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>

        {/* Icon grid (virtualised) or loading spinner */}
        {!ready ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <VirtualIconGrid
            icons={filtered}
            selected={value}
            onSelect={handleSelect}
          />
        )}
      </Popover>
    </>
  )
}

export const TagsTab: FC = () => {
  const { t } = useTranslation()
  const { data: tags, isLoading, refetch, isFetching } = useGetAllTagsQuery()
  const [createTag] = useCreateTagMutation()
  const [updateTag] = useUpdateTagMutation()
  const [deleteTag] = useDeleteTagMutation()

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<TagFormData>(emptyForm)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  const handleCreate = useCallback(() => {
    setEditingId(null)
    setForm(emptyForm)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((tag: any) => {
    setEditingId(tag.id)
    setForm({
      label: tag.label ?? "",
      shortLabel: tag.shortLabel ?? "",
      type: tag.type ?? "ORDER",
      color: tag.color ?? "#1976d2",
      icon: tag.icon ?? "fa-tag",
      displayOption: tag.displayOption ?? "",
      onMobile: tag.onMobile ?? false,
    })
    setFormOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormOpen(false)
    setEditingId(null)
    setForm(emptyForm)
  }, [])

  const handleFormSubmit = async () => {
    const body: Record<string, any> = {
      label: form.label,
      type: form.type,
      color: form.color,
      icon: form.icon,
      shortLabel: form.shortLabel || undefined,
      displayOption: form.displayOption || undefined,
      onMobile: form.onMobile,
    }
    if (editingId) {
      await updateTag({ id: editingId, body })
    } else {
      await createTag(body as any)
    }
    handleFormClose()
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      await deleteTag(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  const rows = tags ?? []
  const hasNoData = rows.length === 0

  return (
    <Box {...uiSurface("UI-DLV2-176")} data-testid="tags-tab" sx={{ p: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t("configuration.tags.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("configuration.tags.subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t("refresh")}>
            <IconButton
              data-testid="tags-refresh-btn"
              onClick={refetch}
              disabled={isFetching}
              sx={{
                color: "text.secondary",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <Refresh
                sx={{
                  animation: isFetching ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            data-testid="tags-create-btn"
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
          >
            {t("configuration.tags.create")}
          </Button>
        </Stack>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <Box
          data-testid="tags-loading"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress />
        </Box>
      ) : hasNoData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Stack
            data-testid="tags-empty-state"
            alignItems="center"
            spacing={2}
            sx={{ maxWidth: 400, textAlign: "center" }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "primary.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <Label sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {t("configuration.tags.empty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("configuration.tags.emptyDescription")}
            </Typography>
            <Button
              data-testid="tags-empty-create-btn"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("configuration.tags.create")}
            </Button>
          </Stack>
        </Box>
      ) : (
        <TableContainer
          data-testid="tags-table"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            "& .MuiTableCell-head": {
              bgcolor: "grey.50",
              fontWeight: 600,
              fontSize: 12,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              py: 1.5,
            },
            "& .MuiTableCell-body": {
              py: 1.5,
            },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 200 }}>
                  {t("configuration.tags.preview")}
                </TableCell>
                <TableCell>{t("configuration.tags.label")}</TableCell>
                <TableCell>{t("configuration.tags.shortLabel")}</TableCell>
                <TableCell sx={{ width: 140 }}>
                  {t("configuration.tags.color")}
                </TableCell>
                <TableCell sx={{ width: 140 }}>
                  {t("configuration.tags.displayOption")}
                </TableCell>
                <TableCell sx={{ width: 100 }}>
                  {t("configuration.tags.type")}
                </TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  {t("configuration.tags.onMobile")}
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  {t("configuration.tags.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((tag: any) => (
                <TableRow
                  key={tag.id}
                  data-testid={`tags-row-${tag.id}`}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {/* Preview: FA icon rendered at actual size in tag color */}
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: tag.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <FaIcon icon={tag.icon} color="#fff" size={16} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {String(t(tag.shortLabel || tag.label, tag.shortLabel || tag.label))}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Label */}
                  <TableCell sx={{ fontWeight: 500 }}>
                    {String(t(tag.label, tag.label))}
                  </TableCell>

                  {/* Short Label */}
                  <TableCell sx={{ color: "text.secondary" }}>
                    {tag.shortLabel ? String(t(tag.shortLabel, tag.shortLabel)) : "—"}
                  </TableCell>

                  {/* Color swatch + hex */}
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          bgcolor: tag.color,
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          color: "text.secondary",
                        }}
                      >
                        {tag.color}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Display Option */}
                  <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                    {tag.displayOption
                      ? String(t(`configuration.tags.displayOption.${
                          tag.displayOption === "ICON_SHORT" ? "both"
                          : tag.displayOption === "ICON" ? "iconOnly"
                          : tag.displayOption === "SHORT" ? "shortOnly"
                          : "both"
                        }`))
                      : "—"}
                  </TableCell>

                  {/* Type */}
                  <TableCell>
                    <Chip
                      label={tag.type}
                      size="small"
                      variant="outlined"
                      color={tag.type === "ORDER" ? "primary" : "secondary"}
                      sx={{ borderRadius: 1, fontWeight: 500, fontSize: 11 }}
                    />
                  </TableCell>

                  {/* On Mobile */}
                  <TableCell align="center">
                    {tag.onMobile ? (
                      <Tooltip title="Visible on mobile">
                        <CheckCircle
                          fontSize="small"
                          color="success"
                          sx={{ fontSize: 18 }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        sx={{ fontSize: 13 }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    {tag.system ? (
                      <Tooltip title={t("configuration.tags.systemTag")}>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ fontStyle: "italic" }}
                        >
                          {t("configuration.tags.system")}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title={t("configuration.tags.edit")}>
                          <IconButton
                            data-testid={`tags-edit-${tag.id}-btn`}
                            size="small"
                            onClick={() => handleEdit(tag)}
                            sx={{
                              color: "primary.main",
                              "&:hover": { bgcolor: "primary.50" },
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("configuration.tags.deleteTitle")}>
                          <IconButton
                            data-testid={`tags-delete-${tag.id}-btn`}
                            size="small"
                            onClick={() => setDeleteConfirmId(tag.id)}
                            sx={{
                              color: "error.main",
                              "&:hover": { bgcolor: "error.50" },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        data-testid="tag-form-dialog"
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ ...uiPaper("UI-DLV2-177"), sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {editingId
            ? t("configuration.tags.edit")
            : t("configuration.tags.create")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1, pt: 1 }}>
            {/* Preview */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent="center"
              sx={{ py: 2, bgcolor: "grey.50", borderRadius: 2 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: form.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaIcon icon={form.icon} color="#fff" size={18} />
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {form.shortLabel || form.label || "Tag Preview"}
              </Typography>
            </Stack>

            <TextField
              data-testid="tag-label-input"
              label={t("configuration.tags.label")}
              fullWidth
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              variant="outlined"
              required
            />

            <TextField
              data-testid="tag-shortLabel-input"
              label={t("configuration.tags.shortLabel")}
              fullWidth
              value={form.shortLabel}
              onChange={(e) => setForm({ ...form, shortLabel: e.target.value })}
              variant="outlined"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                data-testid="tag-type-select"
                select
                label={t("configuration.tags.type")}
                fullWidth
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                variant="outlined"
              >
                <MenuItem value="ORDER">ORDER</MenuItem>
                <MenuItem value="CLIENT">CLIENT</MenuItem>
              </TextField>

              <TextField
                data-testid="tag-displayOption-select"
                select
                label={t("configuration.tags.displayOption")}
                fullWidth
                value={form.displayOption}
                onChange={(e) =>
                  setForm({ ...form, displayOption: e.target.value })
                }
                variant="outlined"
              >
                <MenuItem value="ICON_SHORT">
                  {t("configuration.tags.displayOption.both")}
                </MenuItem>
                <MenuItem value="ICON">
                  {t("configuration.tags.displayOption.iconOnly")}
                </MenuItem>
                <MenuItem value="SHORT">
                  {t("configuration.tags.displayOption.shortOnly")}
                </MenuItem>
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <IconPicker
                  value={form.icon}
                  color={form.color}
                  onChange={(icon) => setForm({ ...form, icon })}
                />
              </Box>

              <TextField
                data-testid="tag-color-input"
                label={t("configuration.tags.color")}
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                variant="outlined"
                sx={{
                  minWidth: 100,
                  "& input": {
                    height: 40,
                    padding: "4px",
                    cursor: "pointer",
                  },
                }}
              />
            </Stack>

            <FormControlLabel
              data-testid="tag-onMobile-switch"
              control={
                <Switch
                  checked={form.onMobile}
                  onChange={(e) =>
                    setForm({ ...form, onMobile: e.target.checked })
                  }
                />
              }
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIphone sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="body2">
                    {t("configuration.tags.onMobile")}
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="tag-form-cancel-btn"
            onClick={handleFormClose}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="tag-form-submit-btn"
            variant="contained"
            onClick={handleFormSubmit}
            disabled={!form.label.trim()}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        data-testid="tag-delete-dialog"
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        PaperProps={{ ...uiPaper("UI-DLV2-179"), sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("configuration.tags.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("configuration.tags.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="tag-delete-cancel-btn"
            onClick={() => setDeleteConfirmId(null)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="tag-delete-confirm-btn"
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
